import { useState } from 'react'
import type { DraftState, PoolsSource } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { controlsAsked, poolsQuestion } from '../../../Services/OrderWizard/questions.ts'
import type { PoolContents } from '../../../Services/OrderWizard/pools.ts'
import { currentPools, poolContents, poolSuggestion } from '../../../Services/OrderWizard/pools.ts'
import { boardLayout, suggestedAnswer, suggestionEdit } from '../../../Services/OrderWizard/poolBoard.ts'
import { PoolAssignment } from './PoolAssignment.tsx'
import { METHOD_TEXT, PoolIntro, PoolsQuestionCard } from './PoolsQuestionCard.tsx'
import { ItemNotes } from './ItemNotes.tsx'
import { usePendingPoolEdit } from './PendingPoolEdit.tsx'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import { samplesWithoutSheetRow } from '../../../Services/OrderWizard/reviewWarnings.ts'
import { sheetMatchOf } from '../../../Services/OrderWizard/sheet/sheetMatching.ts'
import { plural } from '../../../Util/plural.ts'
import { samePartition, sheetPoolsOf, sheetSuggestion } from '../../../Services/OrderWizard/sheet/sheetRoles.ts'
import { sampleDisplayName, sheetTypeDisagreements } from '../../../Services/OrderWizard/sampleLabels.ts'
import { useAnswer } from './useAnswer.ts'

const SOURCE_TEXT: Record<PoolsSource, string> = {
  one: 'confirmed',
  entered: 'split by hand',
  dropped: 'files dropped for each pool',
  wells: `as ${METHOD_TEXT.wells} suggest`,
  'sample-numbers': `as ${METHOD_TEXT['sample-numbers']} suggest`,
  sheet: `from ${METHOD_TEXT.sheet}`
}

const NO_FLAGS: ReadonlySet<string> = new Set()

const LINK = 'btn btn-link btn-sm h-auto min-h-0 px-0'

/**
 * The pools as they are, in one quiet line when nothing is asked: one pool by default, with the ways to
 * split it; or the pools the lab made, and where they came from.
 */
const PoolsLine = ({
  draft,
  contents,
  layout,
  splitting,
  onSplit
}: {
  draft: DraftState
  contents: PoolContents[]
  layout: 'list' | 'board'
  /** Whether the lab asked to split one pool and has moved nothing yet. */
  splitting: boolean
  onSplit: (split: boolean) => void
}) => {
  const dispatch = useDispatch()
  const several = contents.length > 1
  return (
    <div className="space-y-1 text-sm text-slate-600" data-testid="wizard-pools-line">
      <p className="flex flex-wrap items-center gap-x-1.5" data-testid="wizard-pools-how">
        {several ? (
          <>
            <span className="text-slate-800" data-testid="wizard-pools-answer">
              {contents.length} pools — {draft.poolsSource ? SOURCE_TEXT[draft.poolsSource] : 'confirmed'}
            </span>
            <span aria-hidden>·</span>
            To change them, drag items between the pools below.
            <span aria-hidden>·</span>
            <button
              type="button"
              className={LINK}
              data-testid="wizard-pools-merge"
              onClick={() => dispatch(wizard.questionAnswered({ draftId: draft.id, answer: { kind: 'pools', choice: 'one' } }))}
            >
              Merge all into one pool
            </button>
          </>
        ) : splitting ? (
          'Drag items onto “New pool” below.'
        ) : (
          <>
            <span className="text-slate-800" data-testid="wizard-pools-answer">
              All items in one pool
            </span>
            <span aria-hidden>·</span>
            <button type="button" className={LINK} data-testid="wizard-pools-choose-split" onClick={() => onSplit(true)}>
              Split into pools…
            </button>
          </>
        )}
        {splitting && (
          <>
            <span aria-hidden>·</span>
            <button type="button" className={LINK} data-testid="wizard-pools-keep-one" onClick={() => onSplit(false)}>
              Keep one pool after all
            </button>
          </>
        )}
      </p>
      {layout === 'board' && <PoolIntro />}
    </div>
  )
}

/** A sheet that names most samples leaves the others out on purpose more often than not: they go in one click, and come back with Re-add. */
const WithoutSheetRow = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const without = samplesWithoutSheetRow(draft)
  const matched = Object.keys(sheetMatchOf(draft)?.byGroup ?? {}).length
  if (without.length === 0 || matched === 0) {
    return null
  }
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-slate-600" data-testid="wizard-without-sheet-row">
      {plural(without.length, 'sample')} {without.length === 1 ? 'has' : 'have'} no row in the sample sheet.
      <button
        type="button"
        className={LINK}
        data-testid="wizard-remove-without-sheet-row"
        onClick={() => dispatch(wizard.itemsRemoved({ draftId: draft.id, groupKeys: without.map((group) => group.key) }))}
      >
        Remove {without.length === 1 ? 'it' : 'them'}
      </button>
    </p>
  )
}

/**
 * Where the sheet's pool column stands: applied, offered, or not usable, and whether the file names'
 * plate runs agree. Evidence only; it never blocks.
 */
const SheetPoolsNote = ({ draft }: { draft: DraftState }) => {
  const answer = useAnswer(draft.id)
  const sheet = sheetPoolsOf(draft)
  const column = draft.sheet?.mapping.pool
  if (!sheet || !column) {
    return null
  }
  const applied = draft.poolsSource === 'sheet'
  const plates = poolSuggestion(draft)
  const platesOnSheet = plates && Object.fromEntries(Object.entries(plates.pools).filter(([key]) => key in sheet.pools))
  const agreement = platesOnSheet && Object.keys(sheet.pools).length > 0 ? samePartition(sheet.pools, platesOnSheet) : undefined
  const reusable = !applied && draft.poolsAnswered && sheet.count > 1
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-slate-600" data-testid="wizard-sheet-pools-note">
      <span>
        Sheet column “{column}”: {sheet.count > 1 ? `${sheet.count} pools` : 'no pools'}
        {applied ? ', used for the pools.' : '.'} {sheet.detection.reason}
      </span>
      {agreement !== undefined && (
        <span data-testid="wizard-sheet-pools-plates">
          {agreement ? 'The plate runs in the file names agree.' : `The file names suggest ${plates?.count} groups instead.`}
        </span>
      )}
      {reusable && (
        <button
          type="button"
          className={LINK}
          data-testid="wizard-sheet-pools-use"
          onClick={() => answer(suggestedAnswer(sheetSuggestion(draft, sheet)))}
        >
          Use the sheet&apos;s pools
        </button>
      )}
    </p>
  )
}

const MAX_NAMED = 5

/** The sheet's types beat the names; where they differ, a quiet note says so. */
const SheetTypeNote = ({ draft }: { draft: DraftState }) => {
  const keys = sheetTypeDisagreements(draft)
  if (keys.length === 0) {
    return null
  }
  const names = keys.slice(0, MAX_NAMED).map((key) => sampleDisplayName(draft, key))
  return (
    <p className="text-xs text-slate-600" data-testid="wizard-sheet-type-note">
      The sample sheet types {plural(keys.length, 'item')} differently from what{' '}
      {keys.length === 1 ? 'its name suggests' : 'their names suggest'}: {names.join(', ')}
      {keys.length > MAX_NAMED ? ', …' : ''}. The sheet is used; mark an item by hand to change it.
    </p>
  )
}

/** The order's items: which are negative controls, and which samples share them (the pools). */
export const PoolsStep = ({
  draft,
  flagged = NO_FLAGS
}: {
  draft: DraftState
  /** Items an issue of this step names. */
  flagged?: ReadonlySet<string>
}) => {
  const [split, setSplit] = useState(false)
  const { edit, setEdit } = usePendingPoolEdit()
  const pending = !!edit
  const question = poolsQuestion(draft)
  const contents = poolContents(draft, currentPools(draft))
  if (contents.length === 0) {
    return <ItemNotes draft={draft} />
  }
  const several = contents.length > 1
  const layout = boardLayout({ split, several, pending, suggested: !!question?.suggestion })
  const editSuggestion = () => {
    const suggested = suggestionEdit(draft, question?.suggestion)
    if (suggested) {
      setEdit(suggested)
    }
  }
  return (
    <section className="space-y-3" data-testid="wizard-pools">
      {question ? (
        <PoolsQuestionCard
          draft={draft}
          question={question}
          contents={contents}
          pending={pending}
          keepLeads={!controlsAsked(draft)}
          onEditSuggestion={editSuggestion}
        />
      ) : (
        !pending && <PoolsLine draft={draft} contents={contents} layout={layout} splitting={split && !several} onSplit={setSplit} />
      )}
      <SheetPoolsNote draft={draft} />
      <SheetTypeNote draft={draft} />
      <PoolAssignment draft={draft} flagged={flagged} layout={layout} />
      <WithoutSheetRow draft={draft} />
      <ItemNotes draft={draft} />
    </section>
  )
}
